<script lang="ts">
  // Surface + View toolbar per INTERFACE-REVIEW.md §5 R1 and §6.
  //
  // Previously these two control groups lived in the global header
  // alongside Identity and Refresh, where they read as page-scoped
  // even though they only affect Catalogue → Matrix (rates sub-tab)
  // and Catalogue → Comparison. The wireframe in §6 places them
  // inline above those two screens, where they sit next to the
  // content they actually drive.
  //
  // Surface answers "which projection of the cube am I previewing?"
  // (Marketing today; Broker and Full Offer are progressively-deeper
  // customer projections, both deferred to phase 2/3). View toggles
  // between the projected aggregate and the raw cube cells. They
  // pair semantically — "which projection, rendered how deep" — and
  // the wireframe keeps them adjacent.

  import { setSurface, setViewDepth, surfaceState } from '../lib/surface.svelte';

  interface Props {
    // The View toggle only affects the Matrix Rates sub-tab and the
    // Comparison surface aggregation. With the Matrix sub-tabs now
    // URL-driven (INTERFACE-REVIEW.md §5 R6), the toolbar can hide
    // View on Matrix sub-tabs where it has no effect — completing
    // the R1 / R5 "controls only show where they apply" rule. Defaults
    // to true so a caller that does not know the route still gets the
    // full toolbar; the route-aware caller in Catalogue.svelte passes
    // the precise condition.
    showView?: boolean;
  }
  const { showView = true }: Props = $props();
</script>

<div class="surface-view-toolbar" aria-label="Surface and view depth">
  <div class="control-group">
    <span class="control-group__label">Surface</span>
    <div class="toggle-group" role="group" aria-label="Customer surface">
      <button
        type="button"
        class:is-active={surfaceState.surface === 'marketing'}
        onclick={() => setSurface('marketing')}
      >Marketing</button>
      <button type="button" disabled aria-disabled="true" title="Broker surface is not enabled in this release">Broker</button>
      <button type="button" disabled aria-disabled="true" title="Full Offer surface is not enabled in this release">Full Offer</button>
    </div>
  </div>

  {#if showView}
    <div class="control-group">
      <span class="control-group__label">View</span>
      <div class="toggle-group" role="group" aria-label="Render depth — public surface or private cube">
        <button
          type="button"
          class:is-active={surfaceState.viewDepth === 'surface'}
          onclick={() => setViewDepth('surface')}
          title="Render the customer-facing aggregation (what visitors to the public site see)"
        >Public Surface</button>
        <button
          type="button"
          class:is-active={surfaceState.viewDepth === 'cube'}
          onclick={() => setViewDepth('cube')}
          title="Render the raw cube cells the operator authors against"
        >Private Cube</button>
      </div>
    </div>
  {/if}
</div>

<style>
  .surface-view-toolbar {
    display: flex;
    align-items: center;
    gap: var(--space-5);
    flex-wrap: wrap;
    margin-bottom: var(--space-4);
    padding: var(--space-2) var(--space-3);
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
  }
  .control-group {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }
  .control-group__label {
    font-size: var(--type-sm);
    color: var(--surface-muted);
  }
</style>
